import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ShieldCheck, KeyRound, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../api/client";
import Brand from "../../components/Brand";

const AdminLogin = () => {
    const { user, login, logout } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    if (user && ["admin", "pharmacist"].includes(user.role)) {
        return <Navigate to="/admin/dashboard" replace />;
    }

    const submit = async (e) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        try {
            const u = await login(email.trim().toLowerCase(), password);
            if (!["admin", "pharmacist"].includes(u.role)) {
                // Revert the session so we don't get redirected
                await logout();
                toast.error("This account does not have admin access.");
                setBusy(false);
                return;
            }
            toast.success(`Welcome back, ${u.full_name || u.email}`);
            navigate("/admin/dashboard");
        } catch (err) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            const code = typeof detail === "object" ? detail?.code : null;
            let msg;
            if (status === 401) msg = "Invalid email or password.";
            else if (code === "ACCOUNT_DISABLED") msg = "This account has been disabled. Contact support.";
            else if (typeof detail === "string") msg = detail;
            else msg = detail?.message || "Sign in failed. Please try again.";
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="grid min-h-screen lg:grid-cols-2" data-testid="admin-login-page">
            {/* Left brand panel */}
            <section className="relative hidden overflow-hidden bg-gradient-to-br from-slate-950 via-brand-900 to-brand-700 lg:flex lg:flex-col">
                <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-brand-500/30 blur-3xl" />
                <div className="absolute -bottom-20 -left-20 h-80 w-80 rounded-full bg-brand-400/20 blur-3xl" />
                <div className="relative flex h-full flex-col justify-between p-12 text-white">
                    <div className="flex items-center gap-3">
                        <Brand size="sm" sublabel="Operator console" to={null} testId="admin-login-brand-desktop" className="!text-white" />
                    </div>
                    <div>
                        <p className="label-uppercase text-white/70">Admin OS</p>
                        <h2 className="mt-2 max-w-md text-3xl font-semibold leading-tight tracking-tight">
                            Where every prescription, parcel and patient flows through one calm console.
                        </h2>
                        <ul className="mt-8 space-y-2 text-sm text-white/80">
                            <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Pharmacist Rx verification queue</li>
                            <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Real-time orders & dispatch</li>
                            <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Inventory, batches & expiry alerts</li>
                            <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Wallet refunds in one click</li>
                        </ul>
                    </div>
                    <p className="text-[11px] text-white/40">© PILLS Healthcare · for licensed operators only</p>
                </div>
            </section>

            {/* Right form panel */}
            <section className="flex items-center justify-center bg-slate-50 p-6">
                <div className="w-full max-w-sm">
                    <div className="mb-8 lg:hidden">
                        <Brand size="sm" sublabel="Admin" to="/admin/login" testId="admin-login-brand-mobile" />
                    </div>
                    <p className="label-uppercase">Operator sign in</p>
                    <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">Welcome back</h1>
                    <p className="mt-1 text-sm text-slate-500">Use your admin or pharmacist account to access the console.</p>

                    <form onSubmit={submit} className="mt-8 space-y-4">
                        <label className="block">
                            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Email</span>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                autoComplete="email"
                                required
                                className="input-clean mt-1.5"
                                placeholder="admin@pills.test"
                                data-testid="admin-login-email"
                            />
                        </label>
                        <label className="block">
                            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Password</span>
                            <div className="relative mt-1.5">
                                <KeyRound className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    autoComplete="current-password"
                                    required
                                    minLength={8}
                                    className="input-clean pl-11"
                                    placeholder="••••••••"
                                    data-testid="admin-login-password"
                                />
                            </div>
                        </label>

                        <button
                            type="submit"
                            disabled={busy}
                            className="btn-brand w-full !rounded-lg"
                            data-testid="admin-login-submit"
                        >
                            {busy ? "Signing in..." : "Sign in to admin"}
                        </button>
                    </form>

                    <div className="mt-8 rounded-xl border border-amber-200/70 bg-amber-50 p-3">
                        <p className="flex items-start gap-2 text-xs text-amber-900">
                            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            <span>Admin sessions are audited. Every state-changing action is logged with your identity.</span>
                        </p>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default AdminLogin;
